'use client';

// Правка ПРЯМО С КАРТОЧКИ (MINIMAL-PRODUCT В5-ОДИН-В-ОДИН, этап «Правка шага»): у ChatPlace
// нет единой модалки «настройки шага» — правят то, во что тапнули. Здесь живут поверхности
// этого пути, по скринам разведки data/chatplace/ui-recon/:
//   • 14-msg-text-modal.png  — ЦЕНТРАЛЬНАЯ модалка текста сообщения: заголовок «Добавить
//     сообщение» + подзаголовок, НАД полем панель форматирования (B / I / S / U / ссылка),
//     ВНУТРИ поля снизу — «{…}» (заглушки данных) и счётчик «132/1000»;
//   • 15-button-drawer.png / 16-button-pay.png — шторка КНОПКИ «Быстрый ответ»
//     («Название кнопки» + «Действие на кнопке»: следующий шаг / ссылка / оплата,
//     у Телеграма ещё «Запрос номера телефона»);
//   • 06-content-types.png  — меню «Добавить контент»;
//   • 17/19 — каталоги «+ Условие» / «+ Действие» (вид с 13.08 — наш: один пузырь,
//     пункт с пояснением, без категорий и поиска; см. CatalogList).
//
// ФОРМАТИРОВАНИЕ ЧЕСТНОЕ: у Телеграма пишем HTML-разметку и `additionalFields.parse_mode
// = 'HTML'` (карточка узла n8n-nodes-base.telegram, sendMessage). Приведение разметки к
// канону Телеграма — чистый @scriptera/ui/scenario/chat-html.ts (юниты там же); каналы без
// разметки (Инстаграм, чат на сайте) панель форматирования не показывают вовсе.
//
// Запись в узел — ТОЛЬКО write* из @scriptera/ui/scenario/chat-step.ts (нетронутый текст
// уходит байт в байт, служебные поля не задеваются). Сохранение — колбэком onSave: его
// даёт use-draft-editor (ветка nodeEdit очереди PUT).
import { useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import {
  CHANNEL_RULES,
  canRedo,
  canUndo,
  canonicalNodeType,
  chatActionsFor,
  chatButtonKinds,
  chatCondGroupsFor,
  chatCondItem,
  chatCondsFor,
  chatContentKinds,
  chatContentNameKey,
  chatPayButtonBlock,
  chatPayIssue,
  chatPayModelOf,
  chatUrlBroken,
  chatValueOf,
  filterCatalog,
  hasTgMarkup,
  historyInit,
  historyPush,
  historyRedo,
  historyUndo,
  payLinkValue,
  plainToTgHtml,
  readChatMessage,
  repairSlotTokens,
  sanitizeTgHtml,
  SLOT_CHIP_CLASS,
  slotChipHtml,
  stripSlotChips,
  tgHtmlToPlain,
  wrapSlotChips,
  writeChatMessage,
  type ChatActionId,
  type ChatActionItem,
  type ChatButton,
  type ChatButtonKind,
  type ChatChannelId,
  type ChatCondChannel,
  type ChatCondGroup,
  type ChatCondGroupId,
  type ChatCondItem,
  type ChatCondPickId,
  type ChatContentKind,
  type ChatMessageModel,
  type ChatPayModel,
  type IgStartEvent,
  type Lang,
  type StepRowIcon,
  type WorkflowNode,
} from '@scriptera/ui/scenario';
import { KBareButton, KInput } from '@scriptera/ui/kit';
import { useSheetAutoFocus } from './drawer';
import { DGroup, DWarn } from './drawer-kit';
import { FBlock, FChoiceBlock, FPane } from './form-kit';
import { PayProductPicker } from './pay-product-picker';
import { PlusGlyph } from './plus-glyph';
import { SettingsHero } from './settings-hero';
import { AttachGlyph, RowGlyph } from './step-content-view';

/** Язык кабинета — на нём подписаны заглушки подстановок в тексте (chat-text.ts). */
function useLang(): Lang {
  return useLocale() === 'en' ? 'en' : 'ru';
}

/** Кнопка ✓ шапки шторки зовёт это (ставит сам редактор). */
export type SubmitRef = MutableRefObject<() => void>;

/** Предел текста сообщения — как у ЧП (счётчик «132/1000»). */
const TEXT_MAX = 1000;

/** Пауза в наборе, после которой начинается НОВЫЙ шаг отмены (К2). */
const TYPING_PAUSE_MS = 700;

// ── Редактор ТЕКСТА сообщения (скрин ЧП 14) ─────────────────────────────────────────

/** Глифы панели форматирования (линейные SVG, канон no-emoji). */
const FMT_GLYPHS: Record<string, ReactNode> = {
  bold: <path d="M7 4h6a4 4 0 0 1 0 8H7zM7 12h7a4 4 0 0 1 0 8H7z" />,
  italic: <path d="M10 4h8M6 20h8M14.5 4 9.5 20" />,
  strikeThrough: <path d="M4 12h16M7.5 8.5C7.5 6 9.5 4.6 12 4.6c2 0 3.6.8 4.3 2M16.5 15c0 2.6-2 4.4-4.5 4.4-2.2 0-4-1-4.6-2.6" />,
  underline: <path d="M7 4v7a5 5 0 0 0 10 0V4M5 20h14" />,
  link: <path d="M10.5 13.5a4 4 0 0 0 5.7 0l2.3-2.3a4 4 0 0 0-5.7-5.7l-1 1M13.5 10.5a4 4 0 0 0-5.7 0l-2.3 2.3a4 4 0 0 0 5.7 5.7l1-1" />,
};

function FmtGlyph({ id }: { id: string }) {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {FMT_GLYPHS[id]}
    </svg>
  );
}

/** Заглушка данных: «{…}» вместо подстановки шага (chat-text.ts). */
function SlotGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 4c-2 0-2.5 1.2-2.5 3s0 3.2-2 4.9c1.9 1.7 2 3.1 2 5s.5 3.1 2.5 3.1M15 4c2 0 2.5 1.2 2.5 3s0 3.2 2 4.9c-1.9 1.7-2 3.1-2 5s-.5 3.1-2.5 3.1" />
      <path d="M9.5 12h.01M12 12h.01M14.5 12h.01" />
    </svg>
  );
}

/** Команды панели: имя команды execCommand ↔ подпись. */
const FMT_CMDS: { cmd: string; labelKey: string }[] = [
  { cmd: 'bold', labelKey: 'chatFmtBold' },
  { cmd: 'italic', labelKey: 'chatFmtItalic' },
  { cmd: 'strikeThrough', labelKey: 'chatFmtStrike' },
  { cmd: 'underline', labelKey: 'chatFmtUnderline' },
];

/** Стрелки отмены/повтора (эталон ЧП 14 — правый угол панели). */
function HistGlyph({ dir }: { dir: 'undo' | 'redo' }) {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {dir === 'undo' ? (
        <>
          <path d="M9 7 4 12l5 5" />
          <path d="M4 12h11a5 5 0 0 1 0 10h-1" />
        </>
      ) : (
        <>
          <path d="M15 7l5 5-5 5" />
          <path d="M20 12H9a5 5 0 0 0 0 10h1" />
        </>
      )}
    </svg>
  );
}

/** Значок кнопки эмодзи — ЛИНЕЙНЫЙ смайл: эмодзи в подписях нашего UI не бывает
 *  (канон no-emoji), эмодзи здесь — только содержимое сообщения клиента. */
function EmojiGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 10h.01M15 10h.01M8.5 14.5a4.5 4.5 0 0 0 7 0" />
    </svg>
  );
}

/**
 * Палитра эмодзи для ТЕКСТА СООБЩЕНИЯ (эталон ЧП 14 — значок смайла под полем).
 * Это содержимое клиента, а не подписи нашего интерфейса: запрет эмодзи в UI (память
 * владельца «No emoji in UI») сюда не относится — там про наши надписи и кнопки.
 * Набор ходовой и одинаковый на всех каналах: Телеграм, Инстаграм и чат на сайте
 * показывают их как есть (в узел уходит обычный текст, разметка не нужна).
 */
const EMOJI_PALETTE: readonly string[] = [
  '😀', '😁', '😂', '🤣', '😊', '😉', '😍', '🥰', '😘', '😎',
  '🤩', '🥳', '🙂', '🙃', '😇', '🤔', '🤗', '🤝', '🙏', '👋',
  '👍', '👎', '👏', '💪', '✌️', '🤞', '☝️', '👇', '👉', '👈',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🔥', '⭐', '🌟', '✨',
  '🎉', '🎁', '🎈', '🏆', '🥇', '💎', '💰', '💳', '🛒', '🛍️',
  '📦', '🚚', '🏷️', '📌', '📍', '📅', '⏰', '⌛', '✅', '❌',
  '⚠️', '❗', '❓', '💡', '📢', '📣', '🔔', '🔎', '📝', '📄',
  '📊', '📈', '💬', '📞', '📱', '✉️', '🔗', '🖥️', '⚙️', '🔒',
  '🚀', '🎯', '🧠', '👀', '🤖', '🐱', '🌸', '☀️', '🌈', '🍀',
  '☕', '🍕', '🍰', '🥂', '🎵', '📷', '🎬', '🏠', '🚗', '✈️',
];

export function MsgTextEditor({
  node,
  submitRef,
  onSave,
}: {
  node: WorkflowNode;
  submitRef: SubmitRef;
  onSave: (params: Record<string, unknown>) => void;
}) {
  const t = useTranslations('cabinet.scenario');
  const lang = useLang();
  const initial = useMemo(() => readChatMessage(node, lang), [node, lang]);
  const box = useRef<HTMLDivElement>(null);
  // Разметка, с которой поле открылось. У размеченного шага текст УЖЕ HTML, у обычного —
  // просто текст (экранируем, чтобы «<» не считался тегом).
  const startHtml = useMemo(
    () => (initial ? (initial.html ? initial.text.text : plainToTgHtml(initial.text.text)) : ''),
    [initial],
  );
  const slots = initial?.text.slots ?? [];
  // К2 (дефект приёмки): в ПОЛЕ заглушка «{имя человека}» — НЕДЕЛИМАЯ фишка. Пока она
  // была обычными буквами, каретка вставала внутрь, и эмодзи или жирный на половине
  // подписи убивали подстановку молча (в узел уходил буквальный текст со скобками).
  const startChips = useMemo(
    () => wrapSlotChips(startHtml, slots.map((s) => s.label)),
    // slots — часть initial, отдельной зависимости не нужно.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startHtml],
  );
  const [len, setLen] = useState(() => tgHtmlToPlain(startHtml).length);
  /** Заглушки, которые пришлось починить при записи (страховка chat-text.ts). */
  const [broken, setBroken] = useState<string[]>([]);
  const [marks, setMarks] = useState<Record<string, boolean>>({});
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [slotsOpen, setSlotsOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  // К2: отмена/повтор — СВОЯ история снимков поля (edit-history.ts). Родная стопка
  // браузера здесь не годится: поле мы правим и программно (эмодзи, заглушка, ссылка),
  // а после подмены innerHTML execCommand('undo') молча перестаёт работать.
  const [hist, setHist] = useState(() => historyInit(startChips));
  // Набор букв копится в ОДИН шаг отмены, пока человек не сделал паузу (тикает таймер).
  const typedAt = useRef<number | null>(null);

  // Содержимое ставим РУКАМИ один раз: дальше полем владеет браузер (contenteditable),
  // React в него не пишет — иначе каретка прыгала бы на каждый ввод.
  useEffect(() => {
    const el = box.current;
    if (el && el.innerHTML === '') el.innerHTML = startChips;
  }, [startChips]);

  // Подсветка активных кнопок панели — по состоянию команд в текущем выделении.
  useEffect(() => {
    const read = (): void => {
      const el = box.current;
      if (!el || !el.contains(document.getSelection()?.anchorNode ?? null)) return;
      const next: Record<string, boolean> = {};
      for (const f of FMT_CMDS) {
        try {
          next[f.cmd] = document.queryCommandState(f.cmd);
        } catch {
          next[f.cmd] = false;
        }
      }
      setMarks(next);
    };
    document.addEventListener('selectionchange', read);
    return () => document.removeEventListener('selectionchange', read);
  }, []);

  const sync = (): void => {
    const html = stripSlotChips(box.current?.innerHTML ?? '');
    setLen(tgHtmlToPlain(html).length);
    // Второй рубеж: заглушку могли повредить мимо фишки (вставка текста из буфера,
    // хитрая правка). Чиним при записи, а человеку говорим сразу.
    setBroken(repairSlotTokens(html, slots).repaired);
  };
  /** Запомнить состояние поля. merge — правка продолжает предыдущую (набор букв). */
  const remember = (merge: boolean): void =>
    setHist((h) => historyPush(h, box.current?.innerHTML ?? '', merge));
  const chipOf = (n: Node | null | undefined): HTMLElement | null => {
    const el = n instanceof HTMLElement ? n : (n?.parentElement ?? null);
    return el?.closest(`.${SLOT_CHIP_CLASS}`) ?? null;
  };
  /**
   * Фишка-заглушка, ВНУТРИ которой стоит каретка или в которую упирается выделение.
   * Клик по неделимому куску браузер превращает в выделение ВСЕЙ фишки — такой клик мы
   * перехватываем раньше (onPointerDown ниже) и ставим каретку рядом.
   */
  const selectionChip = (): HTMLElement | null => {
    const sel = document.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const r = sel.getRangeAt(0);
    const at = (c: Node, off: number): Node | null =>
      c.nodeType === 1 ? (c.childNodes[off] ?? c.childNodes[off - 1] ?? null) : c;
    if (r.collapsed) return chipOf(r.startContainer);
    return (
      chipOf(r.startContainer) ??
      chipOf(r.endContainer) ??
      chipOf(at(r.startContainer, r.startOffset)) ??
      chipOf(at(r.endContainer, r.endOffset - 1))
    );
  };
  /**
   * Увести каретку ИЗ заглушки: писать внутрь подстановки нельзя (сломается), поэтому
   * вставка падает СРАЗУ ЗА фишкой. Без этого клик по фишке выделял её целиком, и первый
   * же символ затирал подстановку (дефект приёмки К2, живая проба).
   */
  const escapeChip = (): void => {
    const chip = selectionChip();
    const sel = document.getSelection();
    if (!chip || !sel) return;
    const after = document.createRange();
    after.setStartAfter(chip);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);
  };
  const run = (cmd: string, value?: string): void => {
    box.current?.focus();
    // Вставку (эмодзи, заглушка, ссылка) уводим из фишки; форматирование выделения фишку
    // ОХВАТЫВАЕТ целиком — так и надо, токен при этом цел.
    if (cmd.startsWith('insert')) escapeChip();
    document.execCommand(cmd, false, value);
    sync();
    // Команда панели — всегда свой шаг отмены (жирный откатывается отдельно от букв).
    remember(false);
    if (typedAt.current !== null) window.clearTimeout(typedAt.current);
    typedAt.current = null;
  };
  /** Поставить в поле снимок истории (отмена/повтор): каретка — в конец текста. */
  const restore = (next: ReturnType<typeof historyUndo>): void => {
    const el = box.current;
    if (!el) return;
    el.innerHTML = next.present;
    setHist(next);
    if (typedAt.current !== null) window.clearTimeout(typedAt.current);
    typedAt.current = null;
    sync();
    el.focus();
    const sel = document.getSelection();
    if (sel) {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };
  const onTyped = (): void => {
    // Буквы, набранные подряд (без паузы), — один шаг отмены: пока тикает таймер, правка
    // «продолжает» предыдущую. Часы тут не нужны — нужна именно пауза.
    remember(typedAt.current !== null);
    if (typedAt.current !== null) window.clearTimeout(typedAt.current);
    typedAt.current = window.setTimeout(() => {
      typedAt.current = null;
    }, TYPING_PAUSE_MS);
    sync();
  };

  // Что сделает крестик/✓ шапки. Ставим в эффекте (без deps — после КАЖДОГО рендера):
  // ссылка всегда свежая, а рефы во время рендера не трогаем.
  useEffect(() => {
    submitRef.current = () => {
      if (!initial) return;
      // Обёртки фишек — только для поля: в канон уходит обычный текст заглушки.
      const html = sanitizeTgHtml(stripSlotChips(box.current?.innerHTML ?? ''));
      // Ничего не поменялось — отдаём значение КАК БЫЛО (в узел уйдёт source дословно,
      // «открыл и закрыл» канон не двигает; нормализация тегов сама по себе не правка).
      if (html === sanitizeTgHtml(startHtml)) {
        onSave(writeChatMessage(node, initial));
        return;
      }
      const withMarkup = hasTgMarkup(html);
      // Разметка нужна только там, где она есть И где канал её понимает.
      const useHtml = withMarkup && initial.supportsFormat;
      const text = useHtml ? html : tgHtmlToPlain(html);
      onSave(writeChatMessage(node, { ...initial, html: useHtml, text: { ...initial.text, text } }));
    };
  });
  if (!initial) return null;

  return (
    <DGroup>
      <div className="scn-txt-box">
        {/* Панель: слева форматирование (только у каналов, которые разметку понимают —
            Телеграм), справа отмена/повтор — они есть ВЕЗДЕ (эталон ЧП 14). */}
        <div className="scn-txt-bar">
          {initial.supportsFormat ? (
            <>
            {FMT_CMDS.map((f) => (
              <button
                key={f.cmd}
                type="button"
                className={marks[f.cmd] ? 'on' : undefined}
                title={t(f.labelKey)}
                aria-label={t(f.labelKey)}
                aria-pressed={!!marks[f.cmd]}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => run(f.cmd)}
              >
                <FmtGlyph id={f.cmd} />
              </button>
            ))}
            <button
              type="button"
              className={linkOpen ? 'on' : undefined}
              title={t('chatFmtLink')}
              aria-label={t('chatFmtLink')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setLinkOpen((v) => !v)}
            >
              <FmtGlyph id="link" />
            </button>
            </>
          ) : null}
          {/* Отмена/повтор (К2): гаснут, когда откатывать/возвращать нечего. */}
          <button
            type="button"
            className="scn-txt-hist"
            disabled={!canUndo(hist)}
            title={t('chatFmtUndo')}
            aria-label={t('chatFmtUndo')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => restore(historyUndo(hist))}
          >
            <HistGlyph dir="undo" />
          </button>
          <button
            type="button"
            className="scn-txt-hist"
            disabled={!canRedo(hist)}
            title={t('chatFmtRedo')}
            aria-label={t('chatFmtRedo')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => restore(historyRedo(hist))}
          >
            <HistGlyph dir="redo" />
          </button>
        </div>

        {/* Адрес ссылки спрашиваем строкой под панелью (у ЧП — та же кнопка-звено). */}
        {linkOpen ? (
          <div className="scn-txt-link">
            <input
              value={linkUrl}
              placeholder={t('chatMsgBtnUrlPh')}
              aria-label={t('chatFmtLink')}
              onChange={(e) => setLinkUrl(e.target.value)}
            />
            <button
              type="button"
              className="btn sm primary"
              disabled={linkUrl.trim() === ''}
              onClick={() => {
                run('createLink', linkUrl.trim());
                setLinkUrl('');
                setLinkOpen(false);
              }}
            >
              {t('chatFmtLinkApply')}
            </button>
          </div>
        ) : null}

        <div
          ref={box}
          className="scn-txt-in"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={t('chatMsgText')}
          data-ph={t('chatMsgTextPh')}
          onPointerDown={(e) => {
            // Тап ПО ЗАГЛУШКЕ: каретка встаёт РЯДОМ (слева или справа — куда ближе), а не
            // «выделить весь неделимый кусок», как решает браузер. Иначе первый же символ
            // затирал подстановку целиком (живая проба дефекта К2).
            const chip = (e.target as HTMLElement | null)?.closest?.(`.${SLOT_CHIP_CLASS}`);
            if (!chip) return;
            e.preventDefault();
            const rect = chip.getBoundingClientRect();
            const r = document.createRange();
            if (e.clientX > rect.left + rect.width / 2) r.setStartAfter(chip);
            else r.setStartBefore(chip);
            r.collapse(true);
            const sel = document.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(r);
            box.current?.focus();
          }}
          onBeforeInput={(e) => {
            // Набор с клавиатуры и вставка из буфера: если выделение задело заглушку —
            // пишем СРАЗУ ЗА ней, а не поверх (удаление не трогаем: убрать заглушку целиком
            // человек вправе).
            // inputType есть НЕ у всякого события ввода: у legacy-textInput (его React
            // подставляет под onBeforeInput) поля нет вовсе, и обработчик падал на
            // `.startsWith` — правка текста карточки роняла редактор и не сохранялась
            // (Р8, сквозная приёмка). Нет типа, но есть данные — это вставка текста.
            const inputType = e.nativeEvent.inputType ?? (e.nativeEvent.data != null ? 'insertText' : '');
            if (!inputType.startsWith('insert') || !selectionChip()) return;
            e.preventDefault();
            escapeChip();
            const data =
              e.nativeEvent.data ?? e.nativeEvent.dataTransfer?.getData('text/plain') ?? '';
            if (data) document.execCommand('insertText', false, data);
            onTyped();
          }}
          onInput={onTyped}
          onBlur={sync}
        />

        <div className="scn-txt-foot">
          {/* Эмодзи в ТЕКСТ сообщения (эталон ЧП 14): содержимое клиента, не наша подпись. */}
          <button
            type="button"
            className={`scn-txt-slot${emojiOpen ? ' on' : ''}`}
            title={t('chatEmojiTitle')}
            aria-label={t('chatEmojiTitle')}
            aria-expanded={emojiOpen}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setEmojiOpen((v) => !v);
              setSlotsOpen(false);
            }}
          >
            <EmojiGlyph />
          </button>
          {/* «{…}» — заглушки данных ЭТОГО шага: их подставит бот. Заглушек нет — кнопки
              тоже нет (мёртвых кнопок не делаем): вставлять было бы нечего. */}
          {slots.length > 0 ? (
            <>
              <button
                type="button"
                className="scn-txt-slot"
                title={t('chatSlotsTitle')}
                aria-label={t('chatSlotsTitle')}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setSlotsOpen((v) => !v);
                  setEmojiOpen(false);
                }}
              >
                <SlotGlyph />
              </button>
              {slotsOpen ? (
                <div className="scn-txt-slots">
                  {slots.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        // Вставляем сразу ФИШКОЙ — иначе свежая заглушка была бы
                        // обычными буквами и её снова можно было бы разрезать.
                        run('insertHTML', slotChipHtml(s.label));
                        setSlotsOpen(false);
                      }}
                    >
                      {`{${s.label}}`}
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
          <span className={`scn-txt-count${len > TEXT_MAX ? ' over' : ''}`}>{`${len}/${TEXT_MAX}`}</span>
        </div>
        {/* Сама палитра — полосой под полем (в шторке телефона места на всплывашку нет). */}
        {emojiOpen ? (
          <div className="scn-txt-emoji" role="group" aria-label={t('chatEmojiTitle')}>
            {EMOJI_PALETTE.map((e) => (
              <button
                key={e}
                type="button"
                title={e}
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => run('insertText', e)}
              >
                {e}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {/* Заглушку всё же повредили (вставка из буфера и т.п.): чиним при записи и
          говорим об этом — молча терять подстановку нельзя (дефект приёмки К2). */}
      {broken.length > 0 ? (
        <div className="scn-warn">
          <span className="scn-warn-ico" aria-hidden>
            <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" aria-hidden>
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7.5v5.5M12 16.3h.01" />
            </svg>
          </span>
          <div className="scn-warn-body">
            <p>{t('chatSlotFixed', { slot: broken.join('», «') })}</p>
          </div>
        </div>
      ) : null}
      {initial.text.isExpression ? <p className="scn-help-field">{t('chatMsgExprHint')}</p> : null}
      {slots.length > 0 ? <p className="scn-help-field">{t('chatMsgSlotHint')}</p> : null}
    </DGroup>
  );
}

// ── Шторка КНОПКИ сообщения «Быстрый ответ» (скрины ЧП 15/16) ────────────────────────

/**
 * Выбор одним из вариантов — рядами с круглой отметкой (как «Действие на кнопке» у ЧП,
 * скрин 15, и «Срабатывать на» в условиях запуска, скрин 13). Общий на все шторки
 * конструктора: копировать разметку выбора нельзя.
 */
export function RadioRows({
  options,
  value,
  onChange,
}: {
  options: Array<{ key: string; label: string }>;
  value: string;
  onChange: (key: string) => void;
}) {
  return (
    <div className="rows" role="radiogroup">
      {options.map((o) => (
        <div
          key={o.key}
          className="row pick"
          role="radio"
          aria-checked={value === o.key}
          tabIndex={0}
          onClick={() => onChange(o.key)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onChange(o.key);
            }
          }}
        >
          <span className={`scn-radio${value === o.key ? ' on' : ''}`} aria-hidden />
          <span className="ov-scn-name">{o.label}</span>
        </div>
      ))}
    </div>
  );
}

/** Подписи типов кнопки — словами ЧП («Действие на кнопке», скрины 15/16). */
const BTN_KIND_KEYS: Record<ChatButtonKind, string> = {
  reply: 'chatBtnNextStep',
  link: 'chatMsgBtnLink',
  pay: 'chatBtnPay',
  phone: 'chatBtnPhone',
};

export function MsgButtonEditor({
  node,
  index,
  payStepName,
  payInitial,
  canPlacePay,
  hasPayCred,
  onConnectPay,
  onSavePay,
  onCancel,
  submitRef,
  onSave,
}: {
  node: WorkflowNode;
  /** Индекс кнопки; равен длине списка — добавляем новую. */
  index: number;
  /**
   * Имя шага ОПЛАТЫ этого сценария (null — шага оплаты нет, он ВСТАНЕТ сам при
   * сохранении — Ф3.3): адрес кнопки «Переход к оплате» — заглушка «{ссылка на
   * оплату}» ИМЕННО того шага.
   */
  payStepName: string | null;
  /** Текущий выбор шага оплаты (что продаём) — стартовое состояние пикера; null — шага нет. */
  payInitial: ChatPayModel | null;
  /** Есть куда поставить/где править шаг «Оплата» (payStepPlan.kind !== 'none'). */
  canPlacePay: boolean;
  /** У клиента подключена платёжная система (Робокасса). */
  hasPayCred: boolean;
  /** «Подключить платёжную систему» из жёлтой плашки — уводит в «Подключения». */
  onConnectPay: () => void;
  /**
   * Сохранение кнопки «Переход к оплате» (Ф3.3): use-draft-editor правит существующий шаг
   * «Оплата» или вставляет новый перед этим сообщением, а параметры кнопки берёт из
   * buildParams(имя шага) — адрес кнопки ссылается на этот шаг.
   */
  onSavePay: (pay: ChatPayModel, buildParams: (stepName: string) => Record<string, unknown>) => void;
  /** Закрыть шторку без записи (✓ шапки при мешающей плашке: сохранять нечего, но и
   *  запирать человека в шторке нельзя — живая проба 30.07). */
  onCancel: () => void;
  submitRef: SubmitRef;
  onSave: (params: Record<string, unknown>) => void;
}) {
  const t = useTranslations('cabinet.scenario');
  const lang = useLang();
  const initial = useMemo(() => readChatMessage(node, lang), [node, lang]);
  const prev: ChatButton | undefined = initial?.buttons[index];
  const [text, setText] = useState(prev?.text ?? '');
  const [kind, setKind] = useState<ChatButtonKind>(prev?.kind ?? 'reply');
  const [url, setUrl] = useState(prev?.url.text ?? '');
  const [pay, setPay] = useState<ChatPayModel>(payInitial ?? chatPayModelOf());
  // Попытка применить без товара/названия/цены: окно остаётся, пикер краснеет (П128 п.11).
  const [payTried, setPayTried] = useState(false);
  // Первичный фокус подписи — только на широком экране: на телефоне поле приезжает
  // вместе со шторкой, и клавиатура, поднятая в полёте, уводит вьюпорт вбок и вверх
  // сразу (см. канон у useSheetAutoFocus).
  const nameRef = useSheetAutoFocus<HTMLInputElement>();

  const buttonsWith = (b: ChatButton | null): ChatButton[] => {
    const list = [...(initial?.buttons ?? [])];
    if (b === null) return list.filter((_, i) => i !== index);
    if (index >= list.length) return [...list, b];
    return list.map((old, i) => (i === index ? b : old));
  };
  // Адрес кнопки по типу: у ссылки — что набрал человек (исходное значение несём из
  // канона: могло быть подстановкой), у остальных пусто; у оплаты адрес считает
  // buildParams ниже — он знает имя шага «Оплата» (существующего или вставленного).
  const urlFor = (k: ChatButtonKind) => {
    if (k === 'link') return { ...(prev?.url ?? chatValueOf()), text: url };
    return chatValueOf();
  };
  // Происхождение кнопки Инстаграма (quick = быстрый ответ, не пузырь) несём как было:
  // правка подписи не должна молча переселять кнопку (IG-MESSAGE-LOOK Э1, правило В5.7).
  const next = (): ChatMessageModel | null =>
    initial
      ? {
          ...initial,
          buttons: buttonsWith({ ...(prev?.quick ? { quick: true } : {}), text, kind, url: urlFor(kind) }),
        }
      : null;
  // К3: чего не хватает кнопке «Переход к оплате» (касса / место под шаг оплаты). Пока
  // мешает — кнопка в сценарий НЕ уходит (та же честность, что у ряда условия с
  // нечисловым значением: мёртвого шага не пишем), а плашка внизу говорит, что делать.
  const payBlock = kind === 'pay' ? chatPayButtonBlock(hasPayCred, canPlacePay) : null;
  // Что сделает крестик/✓ шапки. Эффект без deps — после КАЖДОГО рендера: ссылка свежая,
  // а рефы во время рендера не трогаем.
  useEffect(() => {
    submitRef.current = () => {
      // Мешает касса или место под шаг оплаты — кнопку не пишем (мёртвой кнопки в
      // сценарии не будет), но шторку закрываем: ✓ обязан работать, иначе выхода нет.
      if (payBlock) {
        onCancel();
        return;
      }
      if (kind === 'pay') {
        // ГАЛОЧКА НЕ ЗАКРЫВАЕТ ОКНО БЕЗ ТОВАРА (владелец 19.09.2026, П128 п.11): мёртвой
        // кнопки «Переход к оплате» в сценарии не будет; красное — только после попытки.
        if (chatPayIssue(pay) !== null) {
          setPayTried(true);
          return;
        }
        // Ф3.3: выбранный товар уезжает в шаг «Оплата» (существующий или новый перед
        // этим сообщением), кнопка получает заглушку «{ссылка на оплату}» того шага.
        onSavePay(pay, (stepName) => {
          if (!initial) return {};
          const payUrl =
            prev?.kind === 'pay' && prev.url.slots.length > 0
              ? prev.url
              : payLinkValue(stepName, lang);
          return writeChatMessage(node, {
            ...initial,
            buttons: buttonsWith({ text, kind: 'pay', url: payUrl }),
          });
        });
        return;
      }
      const m = next();
      if (m) onSave(writeChatMessage(node, m));
    };
  });
  if (!initial) return null;

  const kinds = chatButtonKinds(node, initial, index);
  // Предел подписи — правило площадки (аудит Ф5.2): быстрая кнопка Инстаграма — до 20
  // символов (узел молча резал бы длиннее), у Телеграма оставляем 64.
  const maxLen = canonicalNodeType(node.type) === 'CUSTOM.instagram' ? 20 : 64;
  // Форма кнопки — на наборе пузырей (FPane + FBlock/FChoiceBlock): те же правила вида,
  // что у «Старта» и мастера шаблона (канон UI-TEMPLATE, «Элементы формы»).
  return (
    <FPane>
      <FBlock label={t('chatBtnName')}>
        <input
          ref={nameRef}
          value={text}
          maxLength={maxLen}
          placeholder={t('chatMsgBtnTextPh')}
          onChange={(e) => setText(e.target.value)}
        />
      </FBlock>
      {/* «Действие на кнопке» — раскрытый список с галочкой: типов кнопки не больше
          четырёх, а выбор из ≤6 вариантов рисуется строками (канон, FChoiceBlock). */}
      <FChoiceBlock
        label={t('chatBtnAction')}
        value={kind}
        onChange={(v) => setKind(v as ChatButtonKind)}
        options={kinds.map((k) => ({ value: k, label: t(BTN_KIND_KEYS[k]) }))}
      />
      {kind === 'link' ? (
        /* Кнопка-ссылка без адреса молча сохранилась бы обычной кнопкой (Ф9); явный
           брак формата (нет http(s), пробел) валит отправку всего сообщения — красным. */
        <FBlock
          label={t('chatMsgBtnUrl')}
          {...(url.trim() === ''
            ? { error: t('chatMsgBtnUrlErr') }
            : chatUrlBroken(url)
              ? { error: t('chatMsgBtnUrlFmtErr') }
              : {})}
          {...(prev && prev.url.slots.length > 0 ? { hint: t('chatMsgSlotHint') } : {})}
        >
          <input value={url} placeholder={t('chatMsgBtnUrlPh')} onChange={(e) => setUrl(e.target.value)} />
        </FBlock>
      ) : null}
      {/* Оплата: всё на месте — пикер «что продаём» (Ф3.3) и серая подсказка про шаг;
          чего-то нет — жёлтая плашка с кнопкой (эталон ЧП 16), и такая кнопка не
          сохраняется, пока не починят. */}
      {kind === 'pay' && payBlock === null ? (
        <>
          <PayProductPicker value={pay} onChange={setPay} tried={payTried} />
          <p className="scn-help-field">
            {payStepName
              ? t('chatBtnPayHint', { step: payStepName })
              : t('chatBtnPayInsertHint')}
          </p>
        </>
      ) : null}
      {payBlock ? (
        <DWarn
          actions={
            payBlock === 'noCred' ? (
              <button type="button" className="btn sm primary" onClick={onConnectPay}>
                {t('chatBtnPayConnect')}
              </button>
            ) : undefined
          }
        >
          <p>{t(payBlock === 'noCred' ? 'chatBtnPayNoCred' : 'chatBtnPayNoPlace')}</p>
        </DWarn>
      ) : null}
      {kind === 'phone' ? <p className="scn-help-field">{t('chatBtnPhoneHint')}</p> : null}
    </FPane>
  );
}

// ── Каталоги «что добавить» (условие, действие, содержимое сообщения) ────────────────
//
// ВИД ОДИН НА ВСЕ ТРИ (заказ владельца 13.08, по скрину каталога условий на телефоне):
//  • категорий НЕТ вовсе — один пузырь, все пункты подряд (подзаголовки «Самые
//    популярные» и прочие с экрана убраны: это не категория, а свалка);
//  • у КАЖДОГО пункта вторая строка серым — коротко, что он умеет;
//  • своей обёртки-карточки у каталога нет: пузырь пунктов лежит прямо в панели
//    (пузырь в пузыре — чужой вид);
//  • строки компактные, по канону рядов списка кабинета (`.set-rows`), чтобы каталог
//    помещался на экран телефона без прокрутки;
//  • поиск показывается, ТОЛЬКО когда пунктов больше двенадцати (см. CATALOG_SEARCH_MIN).

interface CatalogRow {
  key: string;
  icon: StepRowIcon;
  name: string;
  /** Вторая строка серым: что этот пункт умеет (обязательна у всех каталогов). */
  hint?: string;
  /** Свой глиф вместо значка ряда (виды контента — те же глифы, что на карточке). */
  glyph?: ReactNode;
  /**
   * Ф15.1: цвет ПУНКТА (css-переменная вида). Красит и значок, и плитку под ним —
   * плитка берёт его подмесом через currentColor, поэтому цвет ставится на саму плитку,
   * а не на обёртку глифа. Не задан — общий серый значок на серой плитке.
   */
  tone?: string;
}

/**
 * Порог поиска (владелец 13.08): поле «Поиск» появляется, только если пунктов БОЛЬШЕ
 * двенадцати. В каталогах условий (8) и содержимого (до 6) их мало — поле там только
 * занимало экран; самый длинный каталог действий (Телеграм, 12) тоже обходится без него.
 */
const CATALOG_SEARCH_MIN = 12;

export function CatalogList({ rows, onPick }: { rows: CatalogRow[]; onPick: (key: string) => void }) {
  const t = useTranslations('cabinet.scenario');
  const [q, setQ] = useState('');
  const searchable = rows.length > CATALOG_SEARCH_MIN;
  const found = searchable ? filterCatalog(rows, q, (r) => r.name) : rows;
  return (
    <div className="pick-shell">
      {searchable ? (
        <div className="pick-top">
          <KInput
            className="pick-search"
            type="search"
            placeholder={t('chatCatSearch')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      ) : null}
      <div className="pick-list">
        {found.length === 0 ? (
          <div className="empty">{t('chatCatEmpty')}</div>
        ) : (
          <div className="rows set-rows scn-cat-rows">
            {found.map((r) => (
              <KBareButton key={r.key} className="row pick" onClick={() => onPick(r.key)}>
                <span className="scn-cat-ico" aria-hidden {...(r.tone ? { style: { color: r.tone } } : {})}>
                  {r.glyph ?? <RowGlyph icon={r.icon} size={22} />}
                </span>
                <span className="scn-cat-text">
                  <span className="ov-scn-name">{r.name}</span>
                  {r.hint ? <span className="scn-cat-hint">{r.hint}</span> : null}
                </span>
              </KBareButton>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Пояснения пунктов меню контента (эталон ЧП 06 — серая строка под названием). */
const CONTENT_HINT_KEYS: Record<ChatContentKind, string> = {
  text: 'chatContentTextHint',
  image: 'chatContentImageHint',
  video: 'chatContentVideoHint',
  audio: 'chatContentAudioHint',
  file: 'chatContentFileHint',
};

/**
 * Вторая строка пункта каталога УСЛОВИЙ: что это условие проверяет, простым языком
 * (заказ владельца 13.08 — вместо категорий у каждого пункта своё пояснение). Ключ на
 * КАЖДЫЙ пункт каталога (У19): пункта без пояснения не бывает — человек читает список
 * глазами и должен понимать, что берёт. «Своего условия» в каталоге нет вовсе.
 *
 * Тем же пояснением подписаны секции и строки-галочки в общих окнах групп (Е1,
 * chat-step-settings): слова про одно и то же условие в двух местах разъехались бы.
 */
export const COND_HINT_KEYS: Record<ChatCondPickId, string> = {
  subscribed: 'condSubscribedHint',
  paid: 'condPaidHint',
  tagsMatched: 'condTagsMatchedHint',
  time: 'condTimeHint',
  date: 'condDateHint',
  weekday: 'condWeekdayHint',
  var: 'condCardValueHint',
  msgText: 'condMsgTextHint',
  phoneWait: 'condPhoneWaitHint',
  sharedPhone: 'condSharedPhoneHint',
  stepOk: 'condStepOkHint',
};

/** Вторая строка пункта каталога ДЕЙСТВИЙ — что бот сделает (тот же заказ 13.08). */
const ACTION_HINT_KEYS: Record<ChatActionId, string> = {
  delay: 'chatActDelayHint',
  askAi: 'chatActAskAiHint',
  addTags: 'chatActAddTagHint',
  removeTags: 'chatActRemoveTagHint',
  remember: 'chatActRememberHint',
  askPhone: 'chatActAskPhoneHint',
  igAskPhone: 'chatActIgAskPhoneHint',
  httpApi: 'chatActHttpHint',
  chatAction: 'chatActTypingHint',
  refLink: 'chatActRefLinkHint',
  igReplyPrivate: 'chatActIgReplyPrivateHint',
  igReplyComment: 'chatActIgReplyCommentHint',
  tgReaction: 'chatActReactionHint',
  igReaction: 'chatActReactionHint',
  tgEditMsg: 'chatActEditMsgHint',
  tgSaveMsg: 'chatActSaveMsgHint',
};

/** Глиф «текст» для меню контента (линейный SVG, канон no-emoji). */
function TextGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 6h14M5 11h14M5 16h9" />
    </svg>
  );
}

/** Синяя кнопка «+ Условие» / «+ Действие» (эталон ЧП 17/18) — вид ЧП, не пунктир. */
export function AddBlue({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="scn-add-blue" onClick={onClick}>
      <span className="scn-add-round" aria-hidden>
        <PlusGlyph />
      </span>
      {label}
    </button>
  );
}

/**
 * «Добавить контент» на карточке сообщения (эталон ЧП 06): текст, изображение, видео,
 * аудио, файл и кнопка под сообщением — с пояснением у каждого пункта, как в ЧП.
 *
 * Состав ЗАВИСИТ ОТ КАНАЛА (chatContentKinds и паспорт кнопок): что канал умеет, то в
 * меню и стоит — вложения по своду правил каналов, «кнопка» только там, где у сообщения
 * кнопки вообще бывают. Мёртвых пунктов не показываем.
 */
export function ContentCatalog({
  channel,
  onPick,
}: {
  channel: ChatChannelId | null;
  onPick: (what: ChatContentKind | 'button') => void;
}) {
  const t = useTranslations('cabinet.scenario');
  const kinds = channel ? chatContentKinds(channel) : (['text'] as ChatContentKind[]);
  // «Кнопка под сообщением» из меню контента не убирается (заказ владельца): это тоже
  // содержимое сообщения, просто живёт не пузырём, а быстрым ответом. НО только в
  // каналах, где кнопки у сообщения существуют (аудит Ф5.2): у чата на сайте кнопок нет
  // вовсе, у формы они живут на самом экране (правятся в его шторке) — пункт там вёл в
  // пустой редактор.
  const btns = channel ? CHANNEL_RULES[channel].buttons : { kind: 'inline' as const, max: null };
  const buttonRow = btns && btns.kind !== 'onPage';
  const rows: CatalogRow[] = [
    ...kinds.map((k) => ({
      key: k,
      icon: 'cond' as StepRowIcon,
      name: t(chatContentNameKey(k)),
      hint: t(CONTENT_HINT_KEYS[k]),
      glyph:
        k === 'text' ? (
          <TextGlyph />
        ) : (
          <AttachGlyph kind={k === 'file' ? 'file' : k === 'image' ? 'image' : k === 'video' ? 'video' : 'audio'} size={22} />
        ),
    })),
    ...(buttonRow
      ? [{ key: 'button', icon: 'tag' as StepRowIcon, name: t('chatContentButton'), hint: t('chatContentButtonHint') }]
      : []),
  ];
  return <CatalogList rows={rows} onPick={(k) => onPick(k as ChatContentKind | 'button')} />;
}

/**
 * Каталог «+ Действие» (скрин ЧП 19). Состав ЗАВИСИТ ОТ КАНАЛА (свод правил каналов,
 * К6): «Запросить номер телефона» и «печатает…» — шаги Телеграма, в сценарии формы,
 * чата на сайте и Инстаграма их нет. И ОТ ТИПА ЗАПУСКА (К37-Z5): ответы на комментарий —
 * только у сценария, который комментарием запускается. Мёртвых пунктов в каталоге не бывает.
 */
export function ActionCatalog({
  channel,
  igEvents,
  onPick,
}: {
  channel: ChatChannelId | null;
  /**
   * Тип запуска Инстаграма у этого сценария (К37-Z5): ответы на комментарий показываем
   * только сценарию, который комментарием и просыпается — иначе отвечать нечему.
   */
  igEvents?: readonly IgStartEvent[] | null;
  onPick: (item: ChatActionItem) => void;
}) {
  const t = useTranslations('cabinet.scenario');
  const items = chatActionsFor(channel, igEvents ?? null);
  const rows = items.map((a) => ({
    key: a.id,
    icon: a.icon,
    name: t(a.nameKey),
    hint: t(ACTION_HINT_KEYS[a.id]),
  }));
  return (
    <CatalogList
      rows={rows}
      onPick={(key) => {
        const item = items.find((a) => a.id === key);
        if (item) onPick(item);
      }}
    />
  );
}

/**
 * Каталог «+ Условие» (скрин ЧП 17). Состав по каналу считает чистое ядро (chatCondsFor):
 * у чата на сайте и формы «Подписки» нет вовсе — подписываться там не на кого (У1).
 *
 * «Подписка» и «Назначенные метки» стоят в каталоге ВСЕГДА (владелец 12.08): им нужен
 * ещё шаг-данные, но куда его поставить в цепочке — наша забота, а не клиента (кладёт
 * кабинет, в цепочку ставит сборка — cond-pair.ts). Прежний отбор по «есть ли куда
 * врезать» убран: клиенту не объяснить, почему пункт пропал.
 *
 * Е1 (владелец 13.08, вечер): каталог ДВУХУРОВНЕВЫЙ — наверху четыре понятные группы
 * (CHAT_COND_GROUPS), внутри «Подписки и оплаты» и «Данных человека» — свои пункты.
 * «Дата и время» и «Состояние диалога» подменю не имеют: их выбор открывает сразу общее
 * окно группы (его собирает chat-step-settings). Состав строк по каналу — как и был.
 */
export function CondCatalog({
  channel,
  group,
  onPick,
  onPickGroup,
}: {
  channel: ChatCondChannel | null;
  /**
   * Открытая ГРУППА (Е1): задана — показываем её пункты (подменю), не задана — верхний
   * уровень из четырёх групп. Уровнями окна конструктора: «←» приводит обратно наверх.
   */
  group?: ChatCondGroupId | null;
  onPick: (item: ChatCondItem) => void;
  /** Выбор группы на верхнем уровне: подменю или общее окно группы — решает вызвавший. */
  onPickGroup?: (group: ChatCondGroup) => void;
}) {
  const t = useTranslations('cabinet.scenario');
  if (!group) {
    const groups = chatCondGroupsFor(channel);
    return (
      <>
        {/* Э8.4: шапка-герой страницы-категории — значок и короткая фраза по центру, вид
            «Уведомлений» (SettingsHero). Заголовок несёт сам герой, поэтому открыватель
            отдаёт шапке пустой титул (канон страниц-героев). */}
        <SettingsHero icon={<RowGlyph icon="cond" size={72} />} title={t('chatCondAdd')} meta={t('condCatHero')} />
        <CatalogList
          rows={groups.map((g) => ({
            key: g.id,
            icon: g.icon,
            name: t(g.nameKey),
            hint: t(g.hintKey),
          }))}
          onPick={(key) => {
            const g = groups.find((x) => x.id === key);
            if (g) onPickGroup?.(g);
          }}
        />
      </>
    );
  }
  const rows = chatCondsFor(channel, group).map((c) => ({
    key: c.id,
    icon: c.icon,
    name: t(c.nameKey),
    hint: t(COND_HINT_KEYS[c.id]),
  }));
  // Подменю группы (Э8.4): своя шапка-герой — значок группы, её имя и её же короткая
  // фраза (та самая, что подписывает группу строкой верхнего уровня: слова про одну
  // категорию в двух местах разъехаться не могут).
  const cat = chatCondGroupsFor(channel).find((g) => g.id === group) ?? null;
  return (
    <>
      {cat ? (
        <SettingsHero icon={<RowGlyph icon={cat.icon} size={72} />} title={t(cat.nameKey)} meta={t(cat.hintKey)} />
      ) : null}
      <CatalogList
        rows={rows}
        onPick={(key) => {
          const item = chatCondItem(key as ChatCondPickId);
          if (item) onPick(item);
        }}
      />
    </>
  );
}
